import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  schema?: object | string;
};

const SITE_IMAGE = 'https://trustedoptions.net/trustedoptions-logo192.png';

const setMetaTag = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const setJsonLd = (schema?: object | string) => {
  let element = document.head.querySelector<HTMLScriptElement>('script#schema-structured-data');

  if (!schema) {
    if (element) {
      element.remove();
    }
    return;
  }

  if (!element) {
    element = document.createElement('script');
    element.setAttribute('type', 'application/ld+json');
    element.setAttribute('id', 'schema-structured-data');
    document.head.appendChild(element);
  }

  element.textContent = typeof schema === 'string' ? schema : JSON.stringify(schema, null, 2);
};

const Seo = ({ title, description, canonicalUrl, ogTitle, ogDescription, ogUrl, schema }: SeoProps) => {
  useEffect(() => {
    document.title = title;
    setMetaTag('meta[name="description"]', 'name', 'description', description);
    setCanonical(canonicalUrl);

    setMetaTag('meta[property="og:title"]', 'property', 'og:title', ogTitle || title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', ogDescription || description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', ogUrl || canonicalUrl);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', SITE_IMAGE);

    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', ogTitle || title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', ogDescription || description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', SITE_IMAGE);

    setJsonLd(schema);
  }, [canonicalUrl, description, ogDescription, ogTitle, ogUrl, schema, title]);

  return null;
};

export default Seo;
